import React, { useCallback } from 'react';

import { twMerge } from 'tailwind-merge';

import { useSearchParams } from 'react-router-dom';

import Typography from 'components/typography/typography';

import Icon from 'components/icon/icon';

import Tooltip from 'components/tooltip/tooltip';

import Button from 'components/button/button';

import VideoPreview from 'components/video-preview';

import { createLocale } from 'locales';

import Header from './header';
import HeaderDialog from './header-dialog';

const localize = createLocale('global');

type FreelancerSignUpPageContainerProps = {
  showLogOut?: boolean;
  showHeader?: boolean;
  headerTitle?: string;
  showGoBackButton?: boolean;
  onGoBackClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  videoInstructions?: {
    url: string;
    videoPreviewImageURL: string;
  };
  dialogInstructions?: {
    shouldShowDialogInstructions?: boolean;
    component: JSX.Element;
  };
};

const FreelancerSignUpPageContainer = (
  props: FreelancerSignUpPageContainerProps,
): JSX.Element => {
  const {
    showHeader,
    headerTitle,
    showGoBackButton,
    onGoBackClick,
    className,
    dialogInstructions,
  } = props;
  const containerClassName = twMerge(
    'flex flex-col bg-background h-screen overflow-y-auto',
  );
  const contentContainerClassName = twMerge(
    'max-w-[320px] h-full w-full',
    className,
  );

  const [searchParams, setSearchParams] = useSearchParams({
    showInstructionsDialog: 'undefined',
    showInstructionsVideo: 'undefined',
  });

  const onLogOutClicked = useCallback(() => {
    window.location.href = '/logout/';
  }, []);

  const showInstructionsDialog = useCallback(() => {
    setSearchParams({
      showInstructionDialog: 'true',
    });
  }, [setSearchParams]);

  const showInstructionsVideo = useCallback(() => {
    setSearchParams({
      showVideoInstructions: 'true',
    });
  }, [setSearchParams]);

  const hideInstructionsDialog = useCallback(() => {
    setSearchParams({
      showInstructionDialog: 'undefined',
    });
  }, [setSearchParams]);

  const hideInstructionsVideo = useCallback(() => {
    const params = {
      showInstructionDialog: dialogInstructions?.shouldShowDialogInstructions
        ? 'true'
        : 'undefined',
      showVideoInstructions: 'undefined',
    };

    setSearchParams(params);
  }, [dialogInstructions?.shouldShowDialogInstructions, setSearchParams]);

  return (
    <div className={containerClassName}>
      {showHeader && (
        <Header
          headerTitle={headerTitle}
          onGoBackClick={onGoBackClick}
          showGoBackButton={showGoBackButton}
        />
      )}
      <div className="px-3">
        {props.videoInstructions &&
          searchParams.get('showVideoInstructions') === 'true' && (
            <div>
              <div className="flex flex-col justify-start items-center mt-4 h-full w-full">
                <div className="max-w-[320px]">
                  {props.dialogInstructions && (
                    <Tooltip
                      content={localize('readInstructionsTooltip')}
                      position="bottom"
                      className="mb-4"
                    >
                      <button
                        type="button"
                        onClick={showInstructionsDialog}
                        className="flex flex-row items-center cursor-pointer"
                      >
                        <Icon icon="file-text" size={16} className="mr-1" />
                        <Typography as="h5" variant="assistive">
                          {localize('readInstructions')}
                        </Typography>
                      </button>
                    </Tooltip>
                  )}
                  <VideoPreview
                    videoPreviewImageURL={
                      props.videoInstructions.videoPreviewImageURL
                    }
                    videoURL={props.videoInstructions.url}
                  />
                  <Button
                    onClick={hideInstructionsVideo}
                    label={localize('start')}
                    variant="primary"
                    className="w-full my-3 flex flex-row items-center justify-center"
                  />
                </div>
              </div>
            </div>
          )}
        {!(
          props.videoInstructions &&
          searchParams.get('showVideoInstructions') === 'true'
        ) && (
          <div className="flex flex-grow items-center flex-col">
            <div className={contentContainerClassName}>
              {props.showLogOut && (
                <Button
                  label={localize('logout').toUpperCase()}
                  variant="transparent"
                  onClick={onLogOutClicked}
                />
              )}
              <div className="flex flex-row gap-x-2 items-center mt-4 justify-center text-gray400">
                {props.dialogInstructions && (
                  <Tooltip
                    content={localize('readInstructionsTooltip')}
                    position="bottom"
                    className="text-gray600"
                  >
                    <button
                      type="button"
                      onClick={showInstructionsDialog}
                      className="flex flex-row items-center cursor-pointer"
                    >
                      <Icon icon="file-text" size={16} className="mr-1" />
                      <Typography as="h5" variant="assistive">
                        {localize('readInstructions')}
                      </Typography>
                    </button>
                  </Tooltip>
                )}

                {props.videoInstructions && (
                  <Tooltip
                    content={localize('watchVideoInstructionsTooltip')}
                    position="bottom"
                  >
                    <button
                      type="button"
                      onClick={showInstructionsVideo}
                      className="flex flex-row items-center cursor-pointer"
                    >
                      <Icon icon="play2" size={16} className="mr-1" />
                      <Typography as="h5" variant="assistive">
                        {localize('watchVideoInstructions')}
                      </Typography>
                    </button>
                  </Tooltip>
                )}
              </div>
              {props.children}
            </div>
          </div>
        )}
      </div>

      {props.dialogInstructions && (
        <HeaderDialog
          onCloseClicked={hideInstructionsDialog}
          isOpen={searchParams.get('showInstructionDialog') === 'true'}
          className="flex md:justify-center items-center max-w-2xl mx-2 overflow-y-auto"
          buttonProps={{
            label: localize('close'),
            onClick: hideInstructionsDialog,
            variant: 'outlined',
          }}
          title={props.headerTitle}
        >
          {props.dialogInstructions.component}
        </HeaderDialog>
      )}
    </div>
  );
};

export default FreelancerSignUpPageContainer;
